import { useState } from 'react';

import { CheckCircleOutlined } from '@ant-design/icons';
import { Card } from 'antd';
import { useTranslation } from 'react-i18next';

import { TiendaPerfectaReportRoute } from 'models/TiendaPerfecta';

import { INCIDENCE_STATS } from '../utils/incidenceStats';
import {
  IncidenceEntry,
  getIncidenceEntries,
  summarizeIncidences,
} from '../utils/incidences';
import { formatTime } from '../utils/time';
import AnimatedNumber from './AnimatedNumber';
import StopDetailModal from './StopDetailModal';

const I18N_BASE = 'scenes.route_report.components.incidences';
const TOP_RANKING = 3;

interface RouteReportIncidencesProps {
  routes: TiendaPerfectaReportRoute[];
  userTimeZone: string;
  /** `kpis.successfulStops`: sin bypass. Ausente (`null`) = dato no disponible. */
  successfulStops?: number | null;
  /** `kpis.scheduledStops`: base del «de N programadas». */
  scheduledStops: number;
}

/**
 * «Seguimiento» (antes «Incidencias del día», TSK-2315): el resumen de bypasses que hoy solo se
 * ve abriendo una parada a la vez desde `RoutesTable`. Consume
 * `getIncidenceEntries` / `summarizeIncidences` de `utils/incidences.ts`:
 * acá no se recalcula ninguna regla, solo se pinta lo que esas funciones ya
 * derivaron y probaron.
 *
 * Es una LISTA, no una `Table` de antd: en la columna angosta donde va a
 * vivir en T3 una tabla pesa más de lo que aporta, y ninguna columna necesita
 * ordenarse ni paginarse — el servidor no publica ningún orden propio para
 * bypasses, así que no hay nada que preservar.
 */
function RouteReportIncidences({
  routes,
  userTimeZone,
  successfulStops,
  scheduledStops,
}: RouteReportIncidencesProps) {
  const { t } = useTranslation();

  const entries = getIncidenceEntries(routes);
  const summary = summarizeIncidences(entries);

  /**
   * Los mismos dos estados separados que `RoutesTable` (`openStop` /
   * `isStopOpen`, ver `RoutesTable.tsx:224-256`): la entrada abierta se
   * conserva aparte de si el modal está visible porque antd desmonta el
   * contenido recién cuando termina la animación de salida. Si `openEntry` se
   * limpiara al cerrar, el encabezado del modal se derrumbaría a la vista
   * durante todo el fundido de salida.
   */
  const [openEntry, setOpenEntry] = useState<IncidenceEntry | null>(null);
  const [isStopOpen, setIsStopOpen] = useState(false);

  const openStopDetail = (entry: IncidenceEntry) => {
    setOpenEntry(entry);
    setIsStopOpen(true);
  };

  const topRanking = summary.ranking.slice(0, TOP_RANKING);
  const restRanking = summary.ranking.slice(TOP_RANKING);
  const restIncidenceCount = restRanking.reduce(
    (acc, item) => acc + item.count,
    0
  );
  // El primero del top-3 es siempre el de mayor cantidad: el ranking ya viene
  // ordenado descendente. Sirve de referencia del 100% para las barras.
  const maxRankingCount = topRanking[0]?.count ?? 0;

  return (
    <Card
      className="route-report-incidences"
      // El head pasa a ser el `title` de `Card` (T5, punto 6), como en
      // `RouteTimeline`/`RoutesTable`: así hereda su mismo `.ant-card-head`
      // en vez de simular una cabecera propia dentro del body. TSK-2329: solo
      // lleva el título; el total y las camionetas afectadas pasaron a los
      // mini KPIs del cuerpo.
      title={
        <div className="route-timeline__head">
          <span className="route-timeline__title">
            {t(`${I18N_BASE}.title`)}
          </span>
        </div>
      }
    >
      <div className="route-report-incidences__body">
        <div className="route-report-incidences__stats">
          <div
            className={`route-report-incidences__stat route-report-incidences__stat--${INCIDENCE_STATS.executed.modifier}`}
          >
            <span className="route-report-incidences__stat-label">
              {t(
                `${I18N_BASE}.stats.${INCIDENCE_STATS.executed.i18nKey}.label`
              )}
            </span>
            <span className="route-report-incidences__stat-value">
              {/* `successfulStops` puede faltar mientras el backend no lo
                  publique: un guion neutro en vez de un `NaN` o de un 0 que
                  afirmaría que no se ejecutó nada. */}
              {successfulStops == null ? (
                '—'
              ) : (
                <AnimatedNumber value={successfulStops} showDelta />
              )}
            </span>
            <span className="route-report-incidences__stat-sub">
              {t(
                `${I18N_BASE}.stats.${INCIDENCE_STATS.executed.i18nKey}.subtext`,
                {
                  count: scheduledStops,
                }
              )}
            </span>
          </div>
          <div
            className={`route-report-incidences__stat route-report-incidences__stat--${INCIDENCE_STATS.incidents.modifier}`}
          >
            <span className="route-report-incidences__stat-label">
              {t(
                `${I18N_BASE}.stats.${INCIDENCE_STATS.incidents.i18nKey}.label`
              )}
            </span>
            <span className="route-report-incidences__stat-value">
              <AnimatedNumber value={summary.total} showDelta />
            </span>
            <span className="route-report-incidences__stat-sub">
              {t(
                `${I18N_BASE}.stats.${INCIDENCE_STATS.incidents.i18nKey}.subtext`,
                {
                  count: summary.affectedRouteCount,
                }
              )}
            </span>
          </div>
        </div>

        {summary.total === 0 ? (
          // Estado propio (T4), no el `Empty` genérico de antd: el
          // mini KPI de Incidencias ya muestra el 0, así que acá alcanza con
          // una sola línea que describa el estado, sin saludo ni relleno.
          <div className="route-report-incidences__empty">
            <CheckCircleOutlined className="route-report-incidences__empty-icon" />
            <p className="route-report-incidences__empty-text">
              {t(`${I18N_BASE}.empty`)}
            </p>
          </div>
        ) : (
          <>
            <div className="route-report-incidences__ranking">
              {topRanking.map((item, index) => (
                <div
                  className="route-report-incidences__ranking-item"
                  key={item.reason}
                >
                  <div className="route-report-incidences__ranking-row">
                    <span className="route-report-incidences__ranking-reason">
                      {item.reason}
                    </span>
                    <span className="route-report-incidences__ranking-count">
                      {item.count}
                    </span>
                  </div>
                  <div className="route-report-incidences__ranking-track">
                    <div
                      className={`route-report-incidences__ranking-fill route-report-incidences__ranking-fill--${
                        index + 1
                      }`}
                      style={{
                        width: `${
                          maxRankingCount
                            ? (item.count / maxRankingCount) * 100
                            : 0
                        }%`,
                      }}
                    />
                  </div>
                </div>
              ))}

              {restRanking.length > 0 && (
                <div className="route-report-incidences__ranking-rest">
                  {t(`${I18N_BASE}.ranking_rest_types`, {
                    count: restRanking.length,
                  })}{' '}
                  ·{' '}
                  {t(`${I18N_BASE}.ranking_rest_incidences`, {
                    count: restIncidenceCount,
                  })}
                </div>
              )}
            </div>

            {/* Encabezado de grilla nuevo (T5, punto 2): sin esto "Móvil Sur
                01" no dice qué es. Vive AFUERA de `&__list` a propósito -- es
                lo que hace que no scrollee con las filas. */}
            <div className="route-report-incidences__table">
              <div className="route-report-incidences__list-head">
                <span>{t(`${I18N_BASE}.columns.unit`)}</span>
                <span>{t(`${I18N_BASE}.columns.incidence`)}</span>
              </div>

              <div className="route-report-incidences__list">
                {entries.map((entry, index) => {
                  const clickable = Boolean(entry.stopId);
                  const teamName =
                    entry.team?.name ??
                    t('scenes.route_report.components.timeline.no_van');

                  return (
                    <div
                      // `stopId` no alcanza solo como key: dos entradas de
                      // días distintos podrían compartirlo si el backend
                      // reutiliza ids, y el índice desempata sin depender de
                      // eso.
                      key={`${entry.stopId ?? 'sin-parada'}-${index}`}
                      className={[
                        'route-report-incidences__row',
                        clickable
                          ? 'route-report-incidences__row--clickable'
                          : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      role={clickable ? 'button' : undefined}
                      tabIndex={clickable ? 0 : undefined}
                      onClick={
                        clickable ? () => openStopDetail(entry) : undefined
                      }
                      onKeyDown={
                        clickable
                          ? (event) => {
                              if (event.key !== 'Enter' && event.key !== ' ') {
                                return;
                              }
                              event.preventDefault();
                              openStopDetail(entry);
                            }
                          : undefined
                      }
                    >
                      {/* Team al frente (T4, decisión de Jorge): se repite en
                          filas consecutivas cuando una unidad acumula varias
                          incidencias, a propósito. `title` con el nombre
                          completo porque la columna trunca -- el dato no se
                          pierde sin aviso. Peso normal y `$text-2` (T5, punto
                          1): en la fila la unidad es dato de contexto, el
                          motivo de abajo es el contenido principal. */}
                      <span
                        className="route-report-incidences__row-team"
                        title={teamName}
                      >
                        {teamName}
                      </span>
                      <div className="route-report-incidences__row-body">
                        {/* Sin `route-report-bypass` (T5, punto 1): ese ámbar
                            tiene sentido en `RoutesTable`, donde una fila con
                            bypass es la excepción entre paradas normales.
                            Acá TODAS las filas son incidencias -- el color no
                            distingue nada y solo hace ruido. Grises y negros;
                            el color queda reservado para el podio del
                            ranking, que sí codifica algo (el puesto). */}
                        <span className="route-report-incidences__row-reason">
                          {entry.reason}
                        </span>
                        <div className="route-report-incidences__row-meta">
                          <span className="route-report-incidences__row-place">
                            {entry.zone ?? t(`${I18N_BASE}.no_zone`)} ·{' '}
                            {entry.label}
                          </span>
                          <span className="route-report-incidences__row-time">
                            {formatTime(entry.finishedAt, userTimeZone) ?? '—'}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Sin `hint`: una `IncidenceEntry` no trae `sequence` ni `routeCode`
          —son datos de la ruta que esta derivación no conserva porque no
          hacen falta ni para el ranking ni para el total— y su `code` es el
          de la incidencia (`bypass.code`), no el de la parada. El encabezado
          del modal arranca con su título genérico y se completa solo cuando
          llega la respuesta del detalle, en vez de afirmar una identidad a
          medias. */}
      <StopDetailModal
        stopId={openEntry?.stopId ?? undefined}
        isOpen={isStopOpen}
        onClose={() => setIsStopOpen(false)}
        userTimeZone={userTimeZone}
      />
    </Card>
  );
}

export default RouteReportIncidences;
